import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type SignedUrlResult = {
  url: string | null;
  reason?: string;
  expires_in: number;
  storage_path?: string;
  version_id?: string;
};

export type WriterCompiledPdfState = {
  loading: boolean;
  /** Blob object URL for the fetched PDF (safe to feed into iframe/object). */
  url: string | null;
  /** PDF bytes for deterministic rendering through PDF.js. */
  bytes: Uint8Array | null;
  /** Raw signed URL (fallback: "Open in new tab" link). */
  signedUrl: string | null;
  reason?: string;
  error?: string;
  version_id?: string;
  storage_path?: string;
  expires_at?: number;
};

/**
 * Cheap content digest (FNV-1a over length + a bounded sample of the bytes).
 * Used to decide whether a refetch actually produced a *different* document.
 * Full-buffer hashing would be wasteful for multi-MB PDFs; sampling the head,
 * tail and a stride through the middle is more than enough to distinguish two
 * compiles of the same project.
 */
export function digestPdfBytes(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  const mix = (byte: number) => {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  };
  const len = bytes.length;
  for (let i = 0; i < 4; i++) mix((len >>> (i * 8)) & 0xff);

  const head = Math.min(len, 4096);
  for (let i = 0; i < head; i++) mix(bytes[i]);
  if (len > 8192) {
    for (let i = len - 4096; i < len; i++) mix(bytes[i]);
    const stride = Math.max(1, Math.floor((len - 8192) / 4096));
    for (let i = 4096; i < len - 4096; i += stride) mix(bytes[i]);
  }
  return `${len.toString(36)}-${hash.toString(36)}`;
}

/**
 * Fetches the compiled PDF for a Writer project and exposes it as a stable
 * blob URL, refreshing well before the signed URL expires. Using a blob URL
 * (instead of pointing an iframe at the raw signed URL) removes two silent-
 * failure modes:
 *   1. Expired-token responses: browsers render nothing without firing
 *      `iframe.onerror`. Here we see the 400 explicitly.
 *   2. Firefox declining to render `application/pdf` inline: blob URLs
 *      behave more predictably across engines.
 *
 * Stability contract (this is what keeps the viewer from flickering):
 * a refetch that yields byte-identical content keeps the *existing*
 * `bytes`/`url` references and only refreshes `signedUrl`/`expires_at`.
 * Consumers can therefore key expensive work (PDF.js parsing, canvas
 * rendering) on `bytes` identity and re-render only on real changes.
 *
 * Refresh strategy: schedule a timer for `expires_in - 30s`, a 30s
 * heartbeat interval while the tab is visible, and a visibility re-check
 * that only refetches when the signed URL is at/near expiry.
 */
export function useWriterCompiledPdf(
  projectId: string | undefined,
  refreshToken: number = 0,
  versionSignature: string = '',
) {
  const [state, setState] = useState<WriterCompiledPdfState>({
    loading: true,
    url: null,
    bytes: null,
    signedUrl: null,
  });
  const blobUrlRef = useRef<string | null>(null);
  const bytesRef = useRef<Uint8Array | null>(null);
  const digestRef = useRef<string | null>(null);
  const expiresAtRef = useRef<number | undefined>(undefined);

  // Revoke the outstanding blob URL only when the hook unmounts. Dependency
  // changes must NOT revoke it: the content may be unchanged and still in use
  // by the viewer.
  useEffect(
    () => () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!projectId) {
      setState({ loading: false, url: null, bytes: null, signedUrl: null, reason: 'no_project' });
      return;
    }
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    let inFlight = false;

    const clearContent = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      bytesRef.current = null;
      digestRef.current = null;
    };

    const fetchOnce = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await supabase.functions.invoke<SignedUrlResult>(
          'writer-compile-proxy',
          { body: { action: 'signed_url', project_id: projectId } },
        );
        const { data, error } = result ?? { data: null, error: null };
        if (cancelled) return;
        if (error) {
          clearContent();
          setState((s) => ({
            ...s,
            loading: false,
            url: null,
            bytes: null,
            error: error.message,
          }));
          return;
        }
        const expiresIn = data?.expires_in ?? 0;
        const signed = data?.url ?? null;
        if (!signed) {
          clearContent();
          expiresAtRef.current = undefined;
          setState({
            loading: false,
            url: null,
            bytes: null,
            signedUrl: null,
            reason: data?.reason,
            version_id: data?.version_id,
            storage_path: data?.storage_path,
          });
          return;
        }

        // Fetch the PDF bytes and mint a blob URL. Surfaces expired-token
        // and network failures as explicit `error` state instead of a
        // silently-blank iframe.
        let blobUrl: string | null = null;
        let bytes: Uint8Array | null = null;
        let fetchError: string | undefined;
        try {
          const res = await fetch(signed);
          if (!res.ok) {
            const bodyPreview = await res.text().catch(() => '');
            fetchError = `HTTP ${res.status}${bodyPreview ? `: ${bodyPreview.slice(0, 160)}` : ''}`;
          } else {
            const buffer = await res.arrayBuffer();
            const header = new TextDecoder('latin1')
              .decode(buffer.slice(0, 5));
            if (header !== '%PDF-') {
              const preview = new TextDecoder('utf-8', { fatal: false })
                .decode(buffer.slice(0, 160));
              fetchError = `Invalid PDF response${preview ? `: ${preview}` : ''}`;
            } else {
              bytes = new Uint8Array(buffer);
            }
            const blob = new Blob([buffer], { type: 'application/pdf' });
            blobUrl = URL.createObjectURL(blob);
          }
        } catch (err) {
          fetchError = err instanceof Error ? err.message : String(err);
        }
        if (cancelled) {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          return;
        }

        const nextExpiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined;
        expiresAtRef.current = nextExpiresAt;

        const digest = bytes ? digestPdfBytes(bytes) : null;
        const unchanged =
          !fetchError &&
          !!bytes &&
          !!digest &&
          digest === digestRef.current &&
          !!bytesRef.current &&
          !!blobUrlRef.current;

        if (unchanged) {
          // Same document: throw away the freshly minted blob and keep every
          // reference the viewer already holds. Only the token moves.
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          setState((s) => ({
            ...s,
            loading: false,
            url: blobUrlRef.current,
            bytes: bytesRef.current,
            signedUrl: signed,
            error: undefined,
            reason: data?.reason,
            version_id: data?.version_id,
            storage_path: data?.storage_path,
            expires_at: nextExpiresAt,
          }));
        } else {
          const previous = blobUrlRef.current;
          blobUrlRef.current = blobUrl;
          bytesRef.current = bytes;
          digestRef.current = digest;
          if (previous) URL.revokeObjectURL(previous);

          setState({
            loading: false,
            url: blobUrl,
            bytes,
            signedUrl: signed,
            error: fetchError,
            reason: data?.reason,
            version_id: data?.version_id,
            storage_path: data?.storage_path,
            expires_at: nextExpiresAt,
          });
        }

        if (expiresIn > 0) {
          const delay = Math.max(5, expiresIn - 30) * 1000;
          if (refreshTimer) clearTimeout(refreshTimer);
          refreshTimer = setTimeout(() => {
            if (!cancelled) void fetchOnce();
          }, delay);
        }
      } finally {
        inFlight = false;
      }
    };

    // Keep whatever is already rendered while the (re)fetch runs — blanking
    // `bytes` here is what used to make the viewer flash on every refresh.
    setState((s) => ({ ...s, loading: true }));
    void fetchOnce();

    const nearExpiry = () => {
      const at = expiresAtRef.current;
      return !at || at - Date.now() < 45_000;
    };

    const onVisible = () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      // Only refetch when the token is actually stale. Coming back to the tab
      // must not re-download and re-render an unchanged document.
      if (nearExpiry()) void fetchOnce();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Heartbeat as a safety net against setTimeout throttling.
    heartbeat = setInterval(() => {
      if (cancelled || document.visibilityState !== 'visible') return;
      if (nearExpiry()) void fetchOnce();
    }, 30_000);

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      if (heartbeat) clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshToken, versionSignature]);

  return state;
}
